import { NEXT_PUBLIC_BASE_URL } from '@/common/core/constants'
import { AuthService } from '@/services/auth.service'
import axios, { type CreateAxiosDefaults } from 'axios'
import { toast } from 'sonner'
import { clearAuthCookies, getAccessToken } from './tokens'

const url = NEXT_PUBLIC_BASE_URL
const options: CreateAxiosDefaults = {
	baseURL: url, //'https://staging.openhere.space/',
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true,
}

const axiosClassic = axios.create(options)

const axiosCanAuthorizedClassic = axios.create(options)

axiosCanAuthorizedClassic.interceptors.request.use(config => {
	const accessToken = getAccessToken(true)

	if (config?.headers && accessToken)
		config.headers.Authorization = `Bearer ${accessToken}`

	return config
})

const axiosWithAuth = axios.create(options)

axiosWithAuth.interceptors.request.use(config => {
	const accessToken = getAccessToken()

	if (config?.headers && accessToken)
		config.headers.Authorization = `Bearer ${accessToken}`

	return config
})

// На странице обычно летит несколько параллельных запросов (account, subscriptions,
// groups, referrals...). Если токен истёк, они все ловят 401 почти одновременно —
// без мьютекса каждый дёргал бы свой /auth/refresh с одним и тем же refresh_token.
// При ротации refresh-токена на бэке выигрывает только первый, остальные получают
// ошибку от /auth/refresh и разлогинивали пользователя, хотя сессия только что
// была успешно обновлена соседним запросом. Держим один общий promise на все
// одновременные обновления — второй и последующие запросы просто ждут первый.
let refreshPromise: ReturnType<typeof AuthService.getNewTokens> | null = null

function refreshTokens() {
	if (!refreshPromise) {
		refreshPromise = AuthService.getNewTokens().finally(() => {
			refreshPromise = null
		})
	}
	return refreshPromise
}

// Когда refresh проваливается, все запросы, ждавшие тот же refreshPromise
// (мьютекс выше), попадают сюда почти одновременно — без этого флага каждый
// из них заново чистил бы куки и показывал свой toast + редирект.
let sessionExpiredHandled = false

function handleSessionExpired() {
	clearAuthCookies()
	if (sessionExpiredHandled) return
	sessionExpiredHandled = true
	setTimeout(() => {
		toast.error('Необходима авторизация')
		setTimeout(() => {
			window.location.href = '/auth/fastregister'
		}, 1000)
	}, 1000)
}

axiosWithAuth.interceptors.response.use(
	config => config,
	async error => {
		const originalRequest = error.config
		const message = errorCatch(error)
		if (
			error.config &&
			!error.config._isRetry &&
			(error?.response?.status === 401 ||
				message === 'jwt expired' ||
				message === 'jwt must be provided')
		) {
			originalRequest._isRetry = true
			try {
				await refreshTokens()
			} catch (refreshError) {
				console.log(refreshError)
				handleSessionExpired()
				return Promise.reject(refreshError)
			}
			try {
				return await axiosWithAuth.request(originalRequest)
			} catch (retryError) {
				if (errorCatch(retryError) === 'jwt expired') {
					handleSessionExpired()
				}
				return Promise.reject(retryError)
			}
		}
		throw error
	}
)

export { axiosCanAuthorizedClassic, axiosClassic, axiosWithAuth }
export const errorCatch = (error: any): string => {
	const message = error?.response?.data?.message

	return message
		? typeof error.response.data.message === 'object'
			? message[0]
			: message
		: error.message
}
